import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import "../../css/signup5.css";
import { useLocation, useNavigate } from "react-router-dom";
import { db } from "../../Utils/firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useRegister, useLogin } from "../../Controllers/AuthController";
import { authLogger } from "../../Utils/logger";
import ContainedButton from "../../Components/Buttons/ContainedButton";
import { remove, retrieve, save } from "../../helpers/StorageHelper";
import { USER_CURRENT_LEVEL } from "../../constants/localStorageKeys";
import { updateUserOnlineStatus } from "../../hooks/useFireStore";
import useAuthStore from "../../zustand-stores/AuthStore";
import useUserStore from "../../zustand-stores/UserStore";

export default function SignUp5() {
  const [occupation, setOccupation] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const image = location.state?.image;

  const registerMutation = useRegister();
  const loginMutation = useLogin();
  const { setAuthSession } = useAuthStore();
  const { setUser } = useUserStore();

  const formData = useMemo(
    () => ({
      full_name: retrieve("full_name") || "",
      email: retrieve("email") || "",
      phone_number: retrieve("mobile") || "",
      password: retrieve("password") || "",
      confirmed_password: retrieve("confirmed_password") || "",
      occupation: occupation,
      image: image,
    }),
    [occupation, image],
  );

  const isFormValid = useMemo(() => occupation.trim() !== "", [occupation]);

  useEffect(() => {
    const savedOccupation = retrieve("occupation");
    if (savedOccupation) setOccupation(savedOccupation);
  }, []);

  const handleOccupationChange = useCallback((e) => {
    const value = e.target.value;
    setOccupation(value);
    save("occupation", value);
  }, []);

  const dataURLtoBlob = useCallback((dataURL) => {
    if (!dataURL) return null;

    const byteString = atob(dataURL.split(",")[1]);
    const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ab], { type: mimeString });
  }, []);

  const handleRegister = useCallback(async () => {
    if (!isFormValid) return;
    setError("");

    try {
      // 1) Prepare form data for registration
      const registrationData = new FormData();
      registrationData.append("full_name", formData.full_name);
      registrationData.append("email", formData.email);
      registrationData.append("phone_number", formData.phone_number);
      registrationData.append("password", formData.password);
      registrationData.append(
        "confirmed_password",
        formData.confirmed_password,
      );
      registrationData.append("occupation", formData.occupation);

      if (formData.image && formData.image !== "") {
        const imageBlob = dataURLtoBlob(formData.image);
        if (imageBlob) {
          const imageFile = new File([imageBlob], "profile_image.jpg", {
            type: imageBlob.type,
          });
          registrationData.append("image", imageFile);
        }
      }

      // 2) Register user (support both response shapes)
      const registerResponse =
        await registerMutation.mutateAsync(registrationData);

      // Some backends return {data: {data: {user, token}}}, some return user directly
      const user =
        registerResponse?.data?.data?.user ??
        registerResponse?.data?.user ??
        registerResponse?.user ??
        registerResponse?.data ??
        registerResponse;

      const token =
        registerResponse?.data?.data?.token ??
        registerResponse?.data?.token ??
        registerResponse?.token;

      if (!user?.id) {
        authLogger.error(
          "Registration response missing user.id",
          registerResponse,
        );
        toast.error("Registration failed, please try again.");
        return;
      }

      authLogger.info("Registration successful", { userId: user.id });
      toast.success("Registration Successful!");

      // 3) Save user to Firebase (use setDoc + merge, use serverTimestamp)
      try {
        await setDoc(
          doc(db, "users", String(user.id)),
          {
            uid: user.id,
            name: user.full_name,
            email: user.email,
            phoneNumber: user.phone_number,
            profileImage: user.image ?? "",
            bio: user.bio ?? "",
            chatStatus: "available",
            isOnline: false,
            lastSeen: serverTimestamp(),
            createdAt: serverTimestamp(),
            settings: {
              lastSeenVisibility: true,
              notifications: true,
              readReceipts: true,
            },
            typing: false,
          },
          { merge: true },
        );
        authLogger.info("Firebase user data saved successfully", {
          userId: user.id,
        });
      } catch (firebaseError) {
        authLogger.error("Failed to save user data to Firebase", firebaseError);
        toast.error("Failed to save user data!");
        // don’t return; registration + login can still proceed
      }

      // 4) Auto-login after registration
      let loginData;
      try {
        loginData = await loginMutation.mutateAsync({
          email: formData.email,
          password: formData.password,
          device_token: "1234flkj",
          device_id: "123",
          device_type: "web",
        });

        authLogger.info("Auto-login successful after registration", {
          userId: loginData?.id ?? user.id,
        });
        toast.success("Login Successful!");
      } catch (loginErr) {
        authLogger.error("Registered but auto-login failed", loginErr);
        toast.error("Registered but login failed. Please login manually.");
        navigate("/login");
        return;
      }

      // 5) Update Firebase online status (use setDoc merge, avoids 'No document to update')
      try {
        await setDoc(
          doc(db, "users", String(user.id)),
          {
            isOnline: true,
            lastSeen: serverTimestamp(),
          },
          { merge: true },
        );
      } catch (firebaseError) {
        authLogger.error("Failed to update user online status", firebaseError);
      }

      // 6) Store user + token (keep what login page expects)
      remove(USER_CURRENT_LEVEL);
      setAuthSession(token);
      setUser(user);
      updateUserOnlineStatus(user, true);

      // 7) Clean up localStorage
      [
        "full_name",
        "email",
        "occupation",
        "image",
        "mobile",
        "password",
        "confirmed_password",
      ].forEach((k) => remove(k));

      // 8) Navigate
      navigate("/notification");
    } catch (err) {
      authLogger.error("Registration failed", err);

      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Registration failed, please try again.";

      toast.error(msg);
      setError(msg);
    }
  }, [
    isFormValid,
    formData,
    dataURLtoBlob,
    registerMutation,
    loginMutation,
    navigate,
  ]);

  return (
    <div className="onboarding-container">
      <div className="onboard-content">
        <div className="d-flex flex-column justify-content-center align-items-center">
          <div className="progress-container">
            <div
              style={{
                background: "url('/icons/progress-tick.svg')",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
              className="step active"
              data-step="1"
            ></div>
            <div className="progress-line active" data-line="1"></div>
            <div
              style={{
                background: "url('/icons/progress-tick.svg')",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
              className="step active"
              data-step="2"
            ></div>
            <div className="progress-line active" data-line="2"></div>
            <div
              style={{
                background: "url('/icons/progress-tick.svg')",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
              className="step active"
              data-step="3"
            ></div>
            <div className="progress-line active" data-line="3"></div>
            <div className="step active stepactive" data-step="4"></div>
          </div>

          <div>
            <h1
              style={{
                marginBottom: "40px",
                textAlign: "center",
                color: "rgba(230, 236, 241, 1)",
                fontFamily: "Poppins",
                fontSize: "20px",
                lineHeight: "30px",
                fontWeight: "600",
              }}
            >
              Add your occupation
              <span style={{ marginLeft: "12px" }}>4 / 4</span>
            </h1>
          </div>

          <div>
            <label htmlFor="nameInput" className="text-body-b2 text-white">
              Type occupation
            </label>
            <input
              type="text"
              id="nameInput"
              className="text-body-b2 styled-input"
              placeholder=" "
              autoComplete="off"
              value={occupation}
              onChange={handleOccupationChange}
            />

            {error && (
              <p style={{ color: "red", marginTop: "16px" }}>{error}</p>
            )}

            <div
              className="d-flex flex-column flex-sm-row gap-3 gap-sm-4 terms-buttons justify-content-between align-items-center w-100"
              style={{
                maxWidth: "345px",
                marginTop: "30px",
                marginBottom: "212px",
              }}
            >
              <ContainedButton
                onClick={() => navigate("/signup/details")}
                className="btn-1 btn-1-basic"
                style={{ maxWidth: "160px", width: "100%" }}
              >
                Back
              </ContainedButton>

              <ContainedButton
                onClick={handleRegister}
                disabled={
                  !isFormValid ||
                  registerMutation.isPending ||
                  loginMutation.isPending
                }
                isLoading={
                  registerMutation.isPending || loginMutation.isPending
                }
                className="btn-1"
                style={{
                  backgroundColor:
                    registerMutation.isPending || loginMutation.isPending
                      ? "rgba(153, 209, 255, 1)"
                      : "rgba(0, 140, 255, 1)",
                  color: "rgba(230, 236, 241, 1)",
                  border: "1.5px solid rgba(0, 140, 255, 1)",
                  maxWidth: "160px",
                  width: "100%",
                  cursor:
                    registerMutation.isPending || loginMutation.isPending
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {registerMutation.isPending || loginMutation.isPending
                  ? "Registering..."
                  : "Done"}
              </ContainedButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
